import { JxtOffCanvasHeader } from '../JxtOffCanvasHeader';
import { Tab, Transition } from '@headlessui/react';
import { IJxtOffCanvas, offCanvasContainerEnum } from './types';
import { Fragment, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { JxtOffCanvasFooter } from '../JxtOffCanvasFooter';
import { leaveMobileAnimationDuration } from '../JxtModal';
import useIsSmallScreen from '../../hooks/useIsSmallScreen';
import { isAddin, SCREEN_SIZE } from '@jooxter/utils';
import { clsx } from 'clsx';
import { IModalAnimation } from '../JxtModal/types';
import { getOffCanvasPanelClasses } from './style';

const desktopAnimation = {
  enter: 'transform transition ease-in duration-200',
  enterFrom: 'translate-x-full',
  enterTo: 'translate-x-0',
  leave: 'transform transition ease-in duration-200',
  leaveFrom: 'translate-x-0',
  leaveTo: 'translate-x-full',
};

const mobileAnimation = {
  enter: 'transition ease-in-out duration-200 transform',
  enterFrom: 'translate-y-full',
  enterTo: 'translate-y-0',
  leave: `transition ease-in-out duration-${leaveMobileAnimationDuration} transform`,
  leaveFrom: 'translate-y-0',
  leaveTo: 'translate-y-full',
};

const MAIN_SELECTOR = 'main';

const JxtOffCanvas = ({
  children,
  header,
  footer,
  show,
  onHide,
  container = offCanvasContainerEnum.Main,
  bodyClass = '',
  tabConfiguration,
}: IJxtOffCanvas) => {
  const isSmallScreen = useIsSmallScreen(SCREEN_SIZE.MD);
  const animation = useMemo<IModalAnimation>(
    () => (isSmallScreen ? mobileAnimation : desktopAnimation),
    [isSmallScreen]
  );
  const [containerElement, setContainerElement] = useState<HTMLElement | null>(null);
  // Desktop: where the container starts in the page (under the navbar), the panel's sticky top.
  const [stickyTop, setStickyTop] = useState(0);
  const { classes } = getOffCanvasPanelClasses(isSmallScreen);

  // `<main>` (or document.body) can still be missing from the DOM on this component's very first
  // render - e.g. right as the route/menu layout is still mounting - so this actively retries via
  // rAF until it succeeds, instead of relying on some unrelated future re-render to happen to try
  // again. It used to be a ref-based one-shot-per-render check with no dependency array: finding
  // the container didn't itself trigger a render, so `content` below (built from this value) stayed
  // stuck on its first, empty computation until *something else* re-rendered this component and
  // picked up the fix. Pages with a lot of incidental re-render churn (Spaces, ManageMyResources)
  // papered over that by accident; a quiet page like Home doesn't, which is what made "the first
  // click doesn't open anything, the second one does" reproducible there specifically.
  useLayoutEffect(() => {
    if (!document) {
      return;
    }

    let frameId: number | undefined;

    const resolveContainer = () => {
      const element = container === offCanvasContainerEnum.Main ? document.querySelector(MAIN_SELECTOR) : document.body;
      if (element instanceof HTMLElement) {
        setContainerElement(element);
      } else {
        frameId = requestAnimationFrame(resolveContainer);
      }
    };

    resolveContainer();

    return () => {
      if (frameId !== undefined) {
        cancelAnimationFrame(frameId);
      }
    };
  }, [container]);

  const sliderClass = isAddin() ? clsx('justify-center items-center', bodyClass) : clsx('px-6 py-4', bodyClass);

  if (containerElement) {
    // `main` clipped without becoming a scroll container: a page's sticky elements stick to the
    // window, which scrolls, not to `main`, which never does.
    containerElement.classList.add(
      'relative',
      container === offCanvasContainerEnum.Main ? 'overflow-clip' : 'overflow-hidden'
    );
  }

  useLayoutEffect(() => {
    if (containerElement && show) {
      setStickyTop(Math.max(0, Math.round(containerElement.getBoundingClientRect().top + window.scrollY)));
    }
  }, [containerElement, show]);

  useEffect(() => {
    if (isSmallScreen) {
      if (show) {
        document.body.classList.add('overflow-hidden');
      } else {
        document.body.classList.remove('overflow-hidden');
      }
    }
  }, [isSmallScreen, show]);

  const portalContent = useMemo(
    () =>
      isSmallScreen ? (
        <>
          {/* z-30, like the modals: above the sticky navbar (z-20), which the backdrop dims. */}
          <Transition.Child as={Fragment} {...animation}>
            <div className="fixed inset-0 z-30 bg-neutral-100/75 transition-opacity" />
          </Transition.Child>
          <Transition.Child as={Fragment} {...animation}>
            <div className="fixed inset-0 z-30 overflow-y-auto">
              <div className="flex min-h-full items-end justify-center p-0 text-center md:items-center">
                {tabConfiguration ? (
                  <Tab.Group
                    as={Fragment}
                    defaultIndex={tabConfiguration?.defaultIndex}
                    selectedIndex={tabConfiguration?.selectedIndex}
                    onChange={tabConfiguration?.onChange}
                  >
                    <div className={classes}>
                      <JxtOffCanvasHeader {...header} onHide={onHide} />
                      <div className={clsx('flex flex-col grow', sliderClass)}>{children}</div>
                      {!!footer?.children && <JxtOffCanvasFooter {...footer} />}
                    </div>
                  </Tab.Group>
                ) : (
                  <div className={classes}>
                    <JxtOffCanvasHeader {...header} onHide={onHide} />
                    <div className={clsx('flex flex-col grow', sliderClass)}>{children}</div>
                    {!!footer?.children && <JxtOffCanvasFooter {...footer} />}
                  </div>
                )}
              </div>
            </div>
          </Transition.Child>
        </>
      ) : (
        // A column as tall as the container, letting clicks through; the panel sticks in it.
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-[400px] max-md:w-full">
          <Transition.Child
            as="div"
            className={classes}
            style={{ top: stickyTop, height: `calc(100vh - ${stickyTop}px)` }}
            {...animation}
          >
            {tabConfiguration ? (
              <Tab.Group
                as={Fragment}
                defaultIndex={tabConfiguration?.defaultIndex}
                selectedIndex={tabConfiguration?.selectedIndex}
                onChange={tabConfiguration?.onChange}
              >
                <JxtOffCanvasHeader onHide={onHide} {...header} />
                <div className={clsx('px-6 py-4 flex flex-col grow', bodyClass)}>{children}</div>
                {!!footer?.children && <JxtOffCanvasFooter {...footer} />}
              </Tab.Group>
            ) : (
              <>
                <JxtOffCanvasHeader onHide={onHide} {...header} />
                <div className={clsx('px-6 py-4 flex flex-col grow', bodyClass)}>{children}</div>
                {!!footer?.children && <JxtOffCanvasFooter {...footer} />}
              </>
            )}
          </Transition.Child>
        </div>
      ),
    [
      isSmallScreen,
      animation,
      tabConfiguration,
      classes,
      header,
      onHide,
      sliderClass,
      children,
      footer,
      bodyClass,
      stickyTop,
    ]
  );

  const content = useMemo(
    () => containerElement && createPortal(portalContent, containerElement),
    [portalContent, containerElement]
  );

  return (
    <Transition show={show} as="div">
      {content}
    </Transition>
  );
};

export default JxtOffCanvas;
